import { z } from 'zod';
import type { UploadMimeType } from '@/types/upload.types';
import { formatFileSize } from '@/utils/number';
import {
  PCMP_MKT_PROOF_MAX_BYTES,
  PCMP_MKT_PROOF_MIME_TYPES,
} from '../constants/pcmpProposal.constants';

const EXTENSION_TYPES: Record<string, UploadMimeType> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  pdf: 'application/pdf',
};

/**
 * A PCMP_DISBURSEMENT upload (payment proof, deposit slip): the browser's type
 * when it is one we take, else the extension's (some report none).
 */
export function resolvePcmpUploadMimeType(file: File): UploadMimeType | null {
  const byType = PCMP_MKT_PROOF_MIME_TYPES.find((type) => type === file.type);
  if (byType) return byType;
  if (file.type !== '') return null;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_TYPES[extension] ?? null;
}

/** A JPEG, PNG or PDF of up to 20 MB, or no file (the caller decides whether one is required). */
export const pcmpUploadFileSchema = z
  .custom<File | null>((value) => value === null || value instanceof File)
  .refine(
    (file) => file === null || resolvePcmpUploadMimeType(file) !== null,
    'The file must be a JPEG, PNG or PDF.',
  )
  .refine(
    (file) => file === null || file.size <= PCMP_MKT_PROOF_MAX_BYTES,
    `File is too large. Maximum allowed size is ${formatFileSize(PCMP_MKT_PROOF_MAX_BYTES)}.`,
  );
